import React, { useMemo, useState } from 'react'
import {
  Box, Button, Checkbox, Divider, InputAdornment, ListItemText, Menu, MenuItem, Popover, Switch, TextField,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined'
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import SearchIcon from '@mui/icons-material/Search'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { C, controlSx } from './tokens'
import { CHANNELS, COLUMNS, SPLIT_HIDDEN, dateShort, filtersOn } from './model'
import { RestaurantLogo } from './Badges'

const KPI_LABEL = {
  needs_driver: 'Needs driver',
  late: 'Late',
  in_transit: 'In transit',
  avg_delivery: "Slower than today's average",
  delivered_today: 'Delivered today',
}

const DATE_PRESETS = [
  { key: 'any', label: 'Any time' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
]

const menuPaper = { borderRadius: '10px', border: `1px solid ${C.border}`, boxShadow: '0 12px 32px -8px rgba(16,24,40,0.2)', mt: 0.5 }

function FilterTrigger({ icon, label, active, onClick }) {
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      startIcon={icon}
      endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '16px !important', opacity: 0.5 }} />}
      sx={{
        ...controlSx,
        maxWidth: 240,
        borderStyle: active ? 'solid' : 'dashed',
        borderColor: active ? 'rgba(16,24,40,0.25)' : C.input,
        color: active ? C.text : C.muted,
        '& .MuiButton-startIcon': { mr: 0.75, '& svg': { fontSize: 16 } },
        '& .MuiButton-endIcon': { ml: 0.5 },
      }}
    >
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
    </Button>
  )
}

function RestaurantFilter({ restaurants, value, onChange }) {
  const [anchor, setAnchor] = useState(null)
  const [q, setQ] = useState('')
  const shown = restaurants.filter(r => !q || r.name.toLowerCase().includes(q.toLowerCase()))
  const label = value.length === 0 ? 'Restaurant' : value.length === 1 ? (restaurants.find(r => r.id === value[0])?.name || '1 restaurant') : `${value.length} restaurants`
  const toggle = id => onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id])
  return (
    <>
      <FilterTrigger icon={<StorefrontOutlinedIcon />} label={label} active={value.length > 0} onClick={e => setAnchor(e.currentTarget)} />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setQ('') }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { ...menuPaper, width: 280 } }}
      >
        <Box sx={{ p: 1, borderBottom: `1px solid ${C.border}` }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Find a restaurant…"
            value={q}
            onChange={e => setQ(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: C.muted }} /></InputAdornment>,
              sx: { fontSize: 13, borderRadius: '8px', height: 34 },
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 300, overflowY: 'auto', py: 0.5 }}>
          {shown.length === 0 && <Box sx={{ px: 2, py: 1.5, fontSize: 13, color: C.muted }}>No restaurant by that name.</Box>}
          {shown.map(r => (
            <MenuItem key={r.id} onClick={() => toggle(r.id)} dense sx={{ gap: 1, mx: 0.5, borderRadius: '6px' }}>
              <Checkbox size="small" checked={value.includes(r.id)} sx={{ p: 0.25, color: C.input, '&.Mui-checked': { color: C.ink } }} />
              <RestaurantLogo restaurant={r} size={20} />
              <Box component="span" sx={{ fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</Box>
            </MenuItem>
          ))}
        </Box>
        {value.length > 0 && (
          <Box sx={{ p: 0.5, borderTop: `1px solid ${C.border}` }}>
            <Button fullWidth size="small" onClick={() => onChange([])} sx={{ textTransform: 'none', color: C.text, fontSize: 13 }}>Clear selection</Button>
          </Box>
        )}
      </Popover>
    </>
  )
}

function ChannelFilter({ value, onChange, channels }) {
  const [anchor, setAnchor] = useState(null)
  const pick = c => { onChange(c); setAnchor(null) }
  return (
    <>
      <FilterTrigger icon={<LocalShippingOutlinedIcon />} label={value === 'ALL' ? 'Channel' : channels[value]} active={value !== 'ALL'} onClick={e => setAnchor(e.currentTarget)} />
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} PaperProps={{ sx: { ...menuPaper, minWidth: 180 } }} MenuListProps={{ dense: true }}>
        {[['ALL', 'All channels'], ...Object.entries(channels)].map(([k, label]) => (
          <MenuItem key={k} onClick={() => pick(k)} sx={{ fontSize: 13, mx: 0.5, borderRadius: '6px', gap: 1 }}>
            <Box sx={{ width: 16, display: 'flex' }}>{value === k && <CheckIcon sx={{ fontSize: 16 }} />}</Box>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

function DateFilter({ value, onChange }) {
  const [anchor, setAnchor] = useState(null)
  const [from, setFrom] = useState(value.from || '')
  const [to, setTo] = useState(value.to || '')
  const fmt = d => dateShort(new Date(`${d}T00:00`).getTime())
  const label =
    value.preset === 'custom'
      ? value.from && value.to && value.from !== value.to ? `${fmt(value.from)} – ${fmt(value.to)}` : value.from ? fmt(value.from) : value.to ? `Until ${fmt(value.to)}` : 'Custom'
      : value.preset === 'any' ? 'Date placed' : DATE_PRESETS.find(d => d.key === value.preset).label
  const t = new Date()
  const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
  return (
    <>
      <FilterTrigger icon={<CalendarTodayOutlinedIcon />} label={label} active={value.preset !== 'any'} onClick={e => setAnchor(e.currentTarget)} />
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} PaperProps={{ sx: { ...menuPaper, width: 260 } }}>
        <Box sx={{ p: 0.5 }}>
          {DATE_PRESETS.map(d => (
            <MenuItem key={d.key} dense onClick={() => { onChange({ preset: d.key }); setAnchor(null) }} sx={{ fontSize: 13, borderRadius: '6px', justifyContent: 'space-between', fontWeight: value.preset === d.key ? 600 : 400 }}>
              {d.label}
              {value.preset === d.key && <CheckIcon sx={{ fontSize: 16 }} />}
            </MenuItem>
          ))}
        </Box>
        <Divider />
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: C.muted }}>Custom range</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField id="dispatch-date-from" type="date" size="small" label="From" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ max: to || today }} sx={{ flex: 1, '& input': { fontSize: 13 } }} />
            <TextField id="dispatch-date-to" type="date" size="small" label="To" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: from || undefined, max: today }} sx={{ flex: 1, '& input': { fontSize: 13 } }} />
          </Box>
          <Button
            variant="contained"
            disableElevation
            disabled={!from && !to}
            onClick={() => { onChange({ preset: 'custom', from: from || null, to: to || null }); setAnchor(null) }}
            sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: C.ink, '&:hover': { bgcolor: '#000' } }}
          >
            Apply range
          </Button>
        </Box>
      </Popover>
    </>
  )
}

/**
 * `restaurants` null hides the restaurant filter (one restaurant's board);
 * `columns` and `channels` are the ones this board has.
 */
export default function FilterBar({ filters, onChange, onClear, restaurants, columns = COLUMNS, channels = CHANNELS, staleCount, density, onDensity, hiddenColumns, onToggleColumn, splitView }) {
  const [colAnchor, setColAnchor] = useState(null)
  const anyFilter = filtersOn(filters)
  const sortedRestaurants = useMemo(() => (restaurants ? [...restaurants].sort((a, b) => a.name.localeCompare(b.name)) : null), [restaurants])

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      {sortedRestaurants && <RestaurantFilter restaurants={sortedRestaurants} value={filters.restaurants} onChange={restaurants => onChange({ restaurants })} />}
      <ChannelFilter value={filters.channel} onChange={channel => onChange({ channel })} channels={channels} />
      <DateFilter value={filters.date} onChange={date => onChange({ date })} />

      <Box
        component="label"
        sx={{
          ...controlSx, display: 'inline-flex', alignItems: 'center', gap: 0.75, pl: 0.5, cursor: 'pointer', border: '1px dashed', userSelect: 'none',
          borderStyle: filters.hideStale ? 'solid' : 'dashed', borderColor: filters.hideStale ? 'rgba(16,24,40,0.25)' : C.input,
          color: filters.hideStale ? C.text : C.muted,
        }}
      >
        <Switch
          size="small"
          checked={filters.hideStale}
          onChange={e => onChange({ hideStale: e.target.checked })}
          inputProps={{ 'aria-label': 'Hide stale orders' }}
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: C.ink, opacity: 1 } }}
        />
        Hide stale
        <Box component="span" sx={{ fontSize: 12, color: C.muted }}>&gt;24h{staleCount > 0 ? ` · ${staleCount}` : ''}</Box>
      </Box>

      {filters.kpi && (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 32, pl: 1.25, pr: 0.5, borderRadius: '8px', bgcolor: C.ink, color: '#fff', fontSize: 13, fontWeight: 500 }}>
          {KPI_LABEL[filters.kpi]}
          <Box component="button" type="button" onClick={() => onChange({ kpi: null })} aria-label={`Remove ${KPI_LABEL[filters.kpi]} filter`} sx={{ display: 'flex', border: 0, bgcolor: 'transparent', color: '#fff', p: 0.25, borderRadius: '4px', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
            <CloseIcon sx={{ fontSize: 15 }} />
          </Box>
        </Box>
      )}

      {anyFilter && (
        <Button size="small" onClick={onClear} sx={{ height: 32, textTransform: 'none', fontSize: 13, color: C.muted, borderRadius: '8px', '&:hover': { bgcolor: C.mutedBg, color: C.text } }}>
          Clear filters
        </Button>
      )}

      <Box sx={{ ml: 'auto', display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={density}
          onChange={(_, v) => v && onDensity(v)}
          aria-label="Row density"
          sx={{ '& .MuiToggleButton-root': { height: 32, width: 34, borderColor: C.border, color: C.muted, '&.Mui-selected': { bgcolor: C.mutedBg, color: C.text } } }}
        >
          {/* No Tooltip wrappers: the group passes selection props to its direct children. */}
          <ToggleButton value="comfortable" aria-label="Comfortable rows" title="Comfortable"><ViewAgendaOutlinedIcon sx={{ fontSize: 17 }} /></ToggleButton>
          <ToggleButton value="compact" aria-label="Compact rows" title="Compact"><ViewHeadlineIcon sx={{ fontSize: 18 }} /></ToggleButton>
        </ToggleButtonGroup>

        <Button variant="outlined" startIcon={<ViewColumnOutlinedIcon sx={{ fontSize: '17px !important' }} />} onClick={e => setColAnchor(e.currentTarget)} sx={controlSx}>
          Columns
        </Button>
        <Menu anchorEl={colAnchor} open={!!colAnchor} onClose={() => setColAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ sx: { ...menuPaper, minWidth: 220 } }} MenuListProps={{ dense: true }}>
          <Box sx={{ px: 2, py: 0.75, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: C.muted }}>Show columns</Box>
          {columns.map(c => {
            const auto = splitView && SPLIT_HIDDEN.includes(c.id)
            return (
              <MenuItem key={c.id} disabled={c.locked || auto} onClick={() => onToggleColumn(c.id)} sx={{ gap: 1, mx: 0.5, borderRadius: '6px' }}>
                <Checkbox size="small" checked={!hiddenColumns.includes(c.id) && !auto} sx={{ p: 0.25, color: C.input, '&.Mui-checked': { color: C.ink } }} />
                <ListItemText primary={c.label} secondary={auto ? 'Hidden in map view' : null} primaryTypographyProps={{ fontSize: 13 }} secondaryTypographyProps={{ fontSize: 11 }} />
              </MenuItem>
            )
          })}
        </Menu>
      </Box>
    </Box>
  )
}
